'use client';

import type { EyewearColorVariant } from '@/lib/product/types';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useRouter } from '@/lib/i18n/navigation';
import styles from './ColorVariantSwitcher.module.scss';

export interface ColorVariantSwitcherProps {
  /** All available color variants for this product */
  colorVariants: EyewearColorVariant[];
  /** Currently selected/displayed variant */
  currentVariant: EyewearColorVariant | null;
  /** URL path pattern for variants - {sku} will be replaced */
  urlPattern?: string;
  /** Callback when a variant is selected (for client-side handling) */
  onVariantSelect?: (variant: EyewearColorVariant) => void;
  /** Whether to show as horizontal swatches or vertical list */
  layout?: 'horizontal' | 'vertical';
  /** Class name for container */
  className?: string;
}

/**
 * Color variant switcher for eyewear PDPs
 * Shows available frame colors as clickable swatches or thumbnails
 */
export function ColorVariantSwitcher({
  colorVariants,
  currentVariant,
  urlPattern,
  onVariantSelect,
  layout = 'horizontal',
  className,
}: ColorVariantSwitcherProps) {
  const router = useRouter();
  const t = useTranslations('product.eyewear');

  const handleVariantClick = (variant: EyewearColorVariant) => {
    if (!variant.available) return;

    if (onVariantSelect) {
      onVariantSelect(variant);
    } else if (urlPattern) {
      // Navigate to the variant's URL
      const url = urlPattern.replace('{sku}', variant.sku);
      router.push(url);
    } else {
      // Default: navigate to same path with different SKU
      const currentPath = window.location.pathname;
      const pathParts = currentPath.split('/');
      pathParts[pathParts.length - 1] = variant.sku;
      router.push(pathParts.join('/'));
    }
  };

  if (colorVariants.length <= 1) {
    // Single color - show color name without selector
    const singleVariant = colorVariants[0];
    if (singleVariant) {
      return (
        <div className={clsx(styles.container, styles.singleColor, className)}>
          <span className={styles.label}>{t('frameColor')}</span>
          <span className={styles.colorName}>{singleVariant.colorName}</span>
        </div>
      );
    }
    return null;
  }

  return (
    <div
      className={clsx(styles.container, styles[layout], className)}
      role="radiogroup"
      aria-label={t('selectColor')}
    >
      <div className={styles.header}>
        <span className={styles.label}>{t('selectColor')}</span>
        {currentVariant && <span className={styles.currentColor}>{currentVariant.colorName}</span>}
      </div>

      <div className={styles.swatches}>
        {colorVariants.map((variant) => {
          const isSelected = currentVariant?.sku === variant.sku;
          const isAvailable = variant.available;

          // Determine if we show a color swatch or an image thumbnail
          const hasColorCode = variant.colorCode;
          const hasImage = variant.image?.url;

          return (
            <button
              key={variant.sku}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={clsx(styles.swatch, {
                [styles.selected]: isSelected,
                [styles.unavailable]: !isAvailable,
                [styles.imageSwatch]: hasImage && !hasColorCode,
              })}
              disabled={!isAvailable}
              onClick={() => handleVariantClick(variant)}
              aria-label={`${variant.colorName}${!isAvailable ? ` - ${t('unavailable')}` : ''}`}
              title={variant.colorName}
            >
              {hasImage && !hasColorCode ? (
                // Image thumbnail swatch
                <div className={styles.imageWrapper}>
                  <Image
                    src={variant.image!.url}
                    alt={variant.image?.alt || variant.colorName}
                    fill
                    sizes="60px"
                    className={styles.swatchImage}
                  />
                </div>
              ) : (
                // Color code swatch (with fallback to gray)
                <span
                  className={styles.colorDot}
                  style={{
                    backgroundColor: variant.colorCode || '#888',
                  }}
                />
              )}

              {/* Selection indicator ring */}
              {isSelected && <span className={styles.selectionRing} />}

              {/* Strikethrough for unavailable */}
              {!isAvailable && <span className={styles.strikethrough} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
